import React, { useState, useMemo, useCallback, useRef, useContext, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, DirectionsRenderer } from '@react-google-maps/api';
import authContext from '../../context/authentication/authContex';
import nivel1Context from '../../context/nivel1Context/nivel1Context';
import nivel2Context from '../../context/nivel2Context/nivel2Context';
import nivel3Context from '../../context/nivel3Context/nivel3Context';
import nivel4Context from '../../context/nivel4Context/nivel4Context';
import nivel5Context from '../../context/nivel5Context/nivel5Context';

import './RutasOPD.css';

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: -34.6037, lng: -58.3816 }; // Buenos Aires

const RutasOPD = ({ opdList, cerrar }) => {
    const [directions, setDirections] = useState(null);
    const [routeInfo, setRouteInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const directionsRequested = useRef(false);

    const { globalSettings } = useContext(authContext);
    const { nivel1Lista } = useContext(nivel1Context);
    const { nivel2List } = useContext(nivel2Context);
    const { nivel3List } = useContext(nivel3Context);
    const { nivel4List } = useContext(nivel4Context);
    const { nivel5List } = useContext(nivel5Context);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    });

    const puntoSalidaGlobal = (globalSettings?.latSalida && globalSettings?.lngSalida)
        ? { lat: globalSettings.latSalida, lng: globalSettings.lngSalida, direccion: globalSettings.direccionSalida }
        : null;

    // Build unified list of niveles with direccion
    const nivelesConDireccion = useMemo(() => {
        const result = [];
        const addFromList = (list, tipo, codField, nameField) => {
            (list || []).forEach(n => {
                if (n.lat && n.lng) {
                    result.push({
                        _id: n._id,
                        tipo,
                        nombre: n[nameField],
                        cod: n[codField],
                        lat: n.lat,
                        lng: n.lng,
                        direccion: n.direccion
                    });
                }
            });
        };
        addFromList(nivel1Lista, 'Nivel1', 'codNivel1', 'name');
        addFromList(nivel2List, 'Nivel2', 'codNivel2', 'name');
        addFromList(nivel3List, 'Nivel3', 'codNivel3', 'nameNivel3');
        addFromList(nivel4List, 'Nivel4', 'codNivel4', 'name');
        addFromList(nivel5List, 'Nivel5', 'codNivel5', 'name');
        return result;
    }, [nivel1Lista, nivel2List, nivel3List, nivel4List, nivel5List]);

    // Origin selector state
    const [selectedOrigin, setSelectedOrigin] = useState(puntoSalidaGlobal ? 'globalSettings' : 'firstOPD');

    // Derive puntoSalida based on selectedOrigin
    const puntoSalida = useMemo(() => {
        if (selectedOrigin === 'globalSettings') {
            return puntoSalidaGlobal;
        }
        if (selectedOrigin === 'firstOPD') {
            return null;
        }
        // It's a nivel _id
        const nivel = nivelesConDireccion.find(n => n._id === selectedOrigin);
        if (nivel) {
            return { lat: nivel.lat, lng: nivel.lng, direccion: nivel.direccion, titulo: `${nivel.tipo} - ${nivel.cod}: ${nivel.nombre}` };
        }
        return null;
    }, [selectedOrigin, puntoSalidaGlobal, nivelesConDireccion]);

    // Reset route when origin changes
    useEffect(() => {
        directionsRequested.current = false;
        setDirections(null);
        setRouteInfo(null);
        setLoading(true);
        setError(null);
    }, [selectedOrigin]);

    const opdsConDireccion = (opdList || []).filter(opd => opd.lat && opd.lng);

    const minimoOPDs = puntoSalida ? 1 : 2;

    const calcularRuta = useCallback(() => {
        if (directionsRequested.current) return;
        if (!isLoaded) return;
        if (opdsConDireccion.length < minimoOPDs) {
            setLoading(false);
            return;
        }
        directionsRequested.current = true;

        try {
            const directionsService = new window.google.maps.DirectionsService();

            let origin, destination, waypoints;

            if (puntoSalida) {
                origin = { lat: puntoSalida.lat, lng: puntoSalida.lng };
                destination = { lat: opdsConDireccion[opdsConDireccion.length - 1].lat, lng: opdsConDireccion[opdsConDireccion.length - 1].lng };
                waypoints = opdsConDireccion.slice(0, -1).map(opd => ({
                    location: { lat: opd.lat, lng: opd.lng },
                    stopover: true
                }));
            } else {
                origin = { lat: opdsConDireccion[0].lat, lng: opdsConDireccion[0].lng };
                destination = { lat: opdsConDireccion[opdsConDireccion.length - 1].lat, lng: opdsConDireccion[opdsConDireccion.length - 1].lng };
                waypoints = opdsConDireccion.slice(1, -1).map(opd => ({
                    location: { lat: opd.lat, lng: opd.lng },
                    stopover: true
                }));
            }

            directionsService.route(
                {
                    origin,
                    destination,
                    waypoints,
                    optimizeWaypoints: true,
                    travelMode: window.google.maps.TravelMode.DRIVING
                },
                (result, status) => {
                    setLoading(false);
                    if (status === 'OK') {
                        setDirections(result);

                        const route = result.routes[0];
                        const waypointOrder = route.waypoint_order;

                        let paradasOrdenadas;
                        if (puntoSalida) {
                            const salidaTitulo = puntoSalida.titulo || 'PUNTO DE SALIDA';
                            const middleOPDs = opdsConDireccion.slice(0, -1);
                            const orderedMiddle = waypointOrder.map(idx => middleOPDs[idx]);
                            paradasOrdenadas = [
                                { tipo: 'salida', titulo: salidaTitulo, direccion: puntoSalida.direccion },
                                ...orderedMiddle.map(opd => ({ tipo: 'opd', titulo: `OPD-${opd.tDocNroFor} - ${opd.title || ''}`, direccion: opd.direccion })),
                                { tipo: 'opd', titulo: `OPD-${opdsConDireccion[opdsConDireccion.length - 1].tDocNroFor} - ${opdsConDireccion[opdsConDireccion.length - 1].title || ''}`, direccion: opdsConDireccion[opdsConDireccion.length - 1].direccion }
                            ];
                        } else {
                            const middleOPDs = opdsConDireccion.slice(1, -1);
                            const orderedMiddle = waypointOrder.map(idx => middleOPDs[idx]);
                            paradasOrdenadas = [
                                opdsConDireccion[0],
                                ...orderedMiddle,
                                opdsConDireccion[opdsConDireccion.length - 1]
                            ].map(opd => ({ tipo: 'opd', titulo: `OPD-${opd.tDocNroFor} - ${opd.title || ''}`, direccion: opd.direccion }));
                        }

                        let totalDistance = 0;
                        let totalDuration = 0;

                        const paradas = paradasOrdenadas.map((parada, i) => {
                            const leg = route.legs[i];
                            if (leg) {
                                totalDistance += leg.distance.value;
                                totalDuration += leg.duration.value;
                            }
                            return {
                                ...parada,
                                distancia: leg ? leg.distance.text : '-',
                                duracion: leg ? leg.duration.text : '-'
                            };
                        });

                        setRouteInfo({
                            paradas,
                            totalDistance: (totalDistance / 1000).toFixed(1) + ' km',
                            totalDuration: Math.round(totalDuration / 60) + ' min'
                        });
                    } else {
                        console.error('Error al calcular ruta:', status, result);
                        setError(`Error de Google Directions: ${status}`);
                    }
                }
            );
        } catch (err) {
            console.error('Error al iniciar DirectionsService:', err);
            setLoading(false);
            setError(`Error: ${err.message}`);
        }
    }, [opdsConDireccion, puntoSalida, minimoOPDs, isLoaded]);

    useEffect(() => {
        if (isLoaded) {
            calcularRuta();
        }
    }, [isLoaded, calcularRuta]);

    if (!isLoaded) {
        return (
            <div className="rutasOPDOverlay">
                <div className="rutasOPDContainer">
                    <div className="rutasOPDHeader">
                        <h3>Rutas Optimizadas</h3>
                        <button className="minimizarBtn" onClick={cerrar}>X</button>
                    </div>
                    <div className="rutasOPDLoading">Cargando Google Maps...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="rutasOPDOverlay">
            <div className="rutasOPDContainer">
                <div className="rutasOPDHeader">
                    <h3>Rutas Optimizadas - {opdsConDireccion.length} OPDs con direccion</h3>
                    <button className="minimizarBtn" onClick={cerrar}>X</button>
                </div>
                {opdsConDireccion.length < minimoOPDs ? (
                    <div className="rutasOPDMensaje">
                        {puntoSalida
                            ? 'Se necesita al menos 1 OPD con direccion validada para calcular una ruta.'
                            : 'Se necesitan al menos 2 OPDs con direccion validada para calcular una ruta (o configure un punto de salida en Configuracion Global o seleccione un nivel con direccion).'}
                        <br />Actualmente hay {opdsConDireccion.length} OPD(s) con direccion.
                    </div>
                ) : (
                    <div className="rutasOPDBody">
                        <div className="rutasOPDMap">
                            <GoogleMap
                                mapContainerStyle={mapContainerStyle}
                                center={defaultCenter}
                                zoom={12}
                            >
                                {directions && <DirectionsRenderer directions={directions} />}
                            </GoogleMap>
                        </div>
                        <div className="rutasOPDSidebar">
                            <div className="rutasOPDOrigenSelector">
                                <label>Punto de origen:</label>
                                <select value={selectedOrigin} onChange={e => setSelectedOrigin(e.target.value)}>
                                    {puntoSalidaGlobal && <option value="globalSettings">Config. Global: {puntoSalidaGlobal.direccion}</option>}
                                    <option value="firstOPD">Primera OPD (sin punto fijo)</option>
                                    {nivelesConDireccion.length > 0 && (
                                        <optgroup label="Niveles con direccion">
                                            {nivelesConDireccion.map(n => (
                                                <option key={n._id} value={n._id}>{n.tipo} - {n.cod}: {n.nombre}</option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                            </div>
                            <h4>Orden Optimizado</h4>
                            {loading && <div className="rutasOPDLoading">Calculando ruta...</div>}
                            {error && <div className="rutasOPDMensaje" style={{ color: 'red' }}>{error}</div>}
                            {routeInfo && (
                                <>
                                    <div className="rutasOPDTotales">
                                        Distancia total: {routeInfo.totalDistance}
                                        <br />
                                        Tiempo estimado: {routeInfo.totalDuration}
                                    </div>
                                    {routeInfo.paradas.map((parada, idx) => {
                                        const letra = String.fromCharCode(65 + idx); // A, B, C...
                                        return (
                                        <div key={idx} className={`rutasOPDParada ${parada.tipo === 'salida' ? 'rutasOPDParadaSalida' : ''}`}>
                                            <span className="rutasOPDParadaIdx">{idx}</span>
                                            <span className="rutasOPDParadaNum">{letra}</span>
                                            <span className="rutasOPDParadaTitulo">{parada.titulo}</span>
                                            <div className="rutasOPDParadaDireccion">{parada.direccion}</div>
                                            {idx < routeInfo.paradas.length - 1 && (
                                                <div className="rutasOPDParadaDistancia">
                                                    Siguiente parada: {parada.distancia} ({parada.duracion})
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RutasOPD;
